import { formatInTimeZone } from "date-fns-tz";
import React from "react";

import { faMemoPad } from "@performyard/fontawesome/pro-regular-svg-icons";
import { faUsers } from "@performyard/fontawesome/pro-solid-svg-icons";
import type { ICohortQueryModel } from "@performyard/models";
import { Icon, Text } from "@performyard/py-components";

import { AutomationRulesDisplay } from "./AutomationRulesDisplay";

import type { AddFormsStepData, ConfiguredForm, FormType } from "../AddFormsStep/types";

import type { CycleType } from "../../Config";
import { STEP_ID as ADD_FORMS_STEP_ID } from "../AddFormsStep/constants";
import { AUTOMATION_RULES_STEP_ID } from "../AutomatedSubjectsStep/constants";
import { AUTOMATION_TYPE_OPTIONS, type AutomationType } from "../CycleSettingsStep/constants";
import { QUALIFYING_SUBJECTS_STEP_ID } from "../QualifyingSubjectsStep/constants";
import { SUBJECTS_STEP_ID } from "../SelectSubjectsStep/constants";

const CYCLE_TYPE_LABELS: Record<CycleType, string> = {
	manual: "Manual Review Cycle",
	automated: "Automated Review Cycle",
	group: "Group Review Cycle",
};

const formatDateTime = (date: Date | null | undefined, timezone: string): string => {
	if (!date) return "—";
	return formatInTimeZone(date, timezone, "MMMM d, yyyy h:mma").replace("AM", "am").replace("PM", "pm");
};

const getAutomationTypeLabel = (value: AutomationType | undefined): string => {
	if (!value) return "—";
	return AUTOMATION_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
};

type SettingsData = {
	cycleName?: string;
	startDate?: Date | null;
	automationType?: AutomationType;
	automationEndDate?: Date | null;
	group?: { id: string; name: string } | null;
};

type Field = { label: string; value: React.ReactNode };

const FieldGrid = ({ fields }: { fields: Field[] }) => (
	<div className="grid grid-cols-1 gap-x-12 gap-y-4 sm:grid-cols-2">
		{fields.map((f) => (
			<div key={f.label} className="flex flex-col gap-1">
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
					{f.label}
				</Text>
				{typeof f.value === "string" || typeof f.value === "number" ? (
					<Text size="paragraph-3" color="neutral-9">
						{f.value}
					</Text>
				) : (
					f.value
				)}
			</div>
		))}
	</div>
);

const flattenForms = (data: AddFormsStepData | undefined): ConfiguredForm<FormType>[] => {
	if (!data) return [];
	return [...data.forms.standard, ...data.forms["360"], ...data.forms.external];
};

const getFormDisplayName = (form: ConfiguredForm<FormType>): string => {
	const name = (form.data as { formName?: string }).formName;
	return name && name.length > 0 ? name : "Untitled Form";
};

type WizardData = Record<string, unknown>;

type ConfirmationStepProps = {
	wizardData: WizardData;
	cycleType: CycleType;
	companyTimezone: string;
};

export const ConfirmationStep = ({ wizardData, cycleType, companyTimezone }: ConfirmationStepProps) => {
	const settings = (wizardData["basic-info"] as SettingsData | undefined) ?? {};
	const formsData = wizardData[ADD_FORMS_STEP_ID] as AddFormsStepData | undefined;
	const subjects = wizardData[SUBJECTS_STEP_ID] as { selectedCount?: number } | undefined;
	const qualifying = wizardData[QUALIFYING_SUBJECTS_STEP_ID] as { total?: number } | undefined;
	const automationRules = wizardData[AUTOMATION_RULES_STEP_ID] as
		| { autoflowUserQuery?: ICohortQueryModel | null }
		| undefined;

	const forms = flattenForms(formsData);
	const templateName = formsData?.selectedTemplate?.name ?? "None";
	const isAutomated = cycleType === "automated";
	const isGroup = cycleType === "group";
	const showAutomationEndDate = isAutomated && settings.automationType === "recurring";

	const subjectCount = isAutomated ? (qualifying?.total ?? 0) : (subjects?.selectedCount ?? 0);
	const subjectLabel = isAutomated
		? subjectCount === 1
			? "1 Qualifying subject at this time"
			: `${subjectCount} Qualifying subjects at this time`
		: subjectCount === 1
			? "1 Employee"
			: `${subjectCount} Employees`;

	const fields: Field[] = [{ label: "Review Cycle Type", value: CYCLE_TYPE_LABELS[cycleType] }];
	if (isAutomated) {
		fields.push({ label: "Automation Type", value: getAutomationTypeLabel(settings.automationType) });
		if (showAutomationEndDate) {
			fields.push({
				label: "Automation End Date",
				value: formatDateTime(settings.automationEndDate, companyTimezone),
			});
			fields.push({ label: "Cycle Start Date", value: formatDateTime(settings.startDate, companyTimezone) });
			fields.push({ label: "Template", value: templateName });
		} else {
			fields.push({ label: "Template", value: templateName });
			fields.push({ label: "Cycle Start Date", value: formatDateTime(settings.startDate, companyTimezone) });
		}
		fields.push({
			label: "Automation Rules",
			value: <AutomationRulesDisplay userQuery={automationRules?.autoflowUserQuery ?? null} />,
		});
	} else {
		fields.push({ label: "Cycle Start Date", value: formatDateTime(settings.startDate, companyTimezone) });
		if (isGroup) {
			fields.push({ label: "Group", value: settings.group?.name ?? "—" });
		}
		fields.push({ label: "Template", value: templateName });
	}

	return (
		<div className="mx-auto mt-4 w-full max-w-3xl rounded border border-solid border-neutral-2 bg-neutral-0 p-6">
			<Text size="sub-heading" weight="semi-bold" color="neutral-9" dangerouslySetClassName="mb-4">
				{settings.cycleName || "Untitled Review Cycle"}
			</Text>

			<div className="rounded bg-neutral-1 p-4">
				<FieldGrid fields={fields} />
			</div>

			<div className="mt-6">
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9" dangerouslySetClassName="mb-2">
					Forms ({forms.length})
				</Text>
				<div className="flex flex-col gap-2">
					{forms.length === 0 ? (
						<Text size="paragraph-3" color="neutral-7">
							No forms added
						</Text>
					) : (
						forms.map((form) => (
							<div key={form.localId} className="flex items-center gap-2 rounded bg-neutral-1 px-3 py-2">
								{form.fromTemplate && <Icon icon={faMemoPad} size={16} color="neutral-7" />}
								<Text size="paragraph-3" color="neutral-9">
									{getFormDisplayName(form)}
								</Text>
							</div>
						))
					)}
				</div>
			</div>

			<div className="mt-6">
				<Text size="paragraph-3" weight="semi-bold" color="neutral-9" dangerouslySetClassName="mb-2">
					Subjects
				</Text>
				<div className="flex items-center gap-2 rounded bg-primary-1 px-3 py-2">
					<Icon icon={faUsers} size={16} color="primary-7" />
					<Text size="paragraph-3" color="neutral-9">
						{subjectLabel}
					</Text>
				</div>
			</div>
		</div>
	);
};
